import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import SettingsPageLayout from './SettingsPageLayout';
import SlidePanel from '../shared/SlidePanel';
import toast from '../shared/Toast';
import ConfirmCard from '../agent/frontdesk/ConfirmCard';
import { prettyNumber, validAreaCode } from '../agent/frontdesk/api';
import { businessNumbersService as api } from '../../_services/businessNumbers.service';
import useSites from '../multiLocation/useSites';
import { hapticError, hapticSuccess, hapticTick } from '../../_utils/haptics';
import './businessNumbers.css';
import { isOwnerHere } from '../permissions/access';

/**
 * Settings › Business numbers: this location's phone numbers, and the job each
 * one does (Charlie, 2026-10-01).
 *
 *   Bailey answers calls  - any number of numbers
 *   Two-way texts         - texts go out from it, client replies come back to
 *                           it; mobiles only, one per location
 *
 * £5 a month each. Bailey's phone page and a client's Messages link here when
 * a location has no number; ?return=<path> brings them back after buying.
 */
const PRICE = '£5 a month';

function Switch({ on, disabled, onChange, label }) {
    return (
        <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled}
            className={`bn-switch${on ? ' is-on' : ''}`} onClick={() => { hapticTick(); onChange(!on); }}>
            <span />
        </button>
    );
}

function BuyPanel({ onClose, onBought }) {
    const sheet = useRef(null);
    const [kind, setKind] = useState('mobile');
    const [area, setArea] = useState('');
    const [options, setOptions] = useState(null);
    const [pick, setPick] = useState('');
    const [label, setLabel] = useState('');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const areaOk = validAreaCode(area);

    const find = async () => {
        setBusy('find'); setError('');
        try {
            const list = await api.options(kind, kind === 'landline' ? area.replace(/\s+/g, '') : '');
            setOptions(list); setPick(list.length === 1 ? list[0].number : '');
        } catch (e) { setError(e.message || 'I couldn’t look for numbers just now.'); }
        setBusy('');
    };
    const buy = async () => {
        if (!pick) return;
        setBusy('buy'); setError('');
        try {
            const r = await api.buy({ kind, phoneNumber: pick, label });
            hapticSuccess();
            toast.success(`${prettyNumber(pick)} is yours`);
            onBought(r && r.numbers);
            if (sheet.current) sheet.current.handleClose(); else onClose();
        } catch (e) { hapticError(); setError(e.message || 'That number couldn’t be taken. Pick another.'); setBusy(''); }
    };

    const footer = options ? (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" disabled={busy === 'buy'} onClick={() => { setOptions(null); setPick(''); }}>Back</button>
            <button type="button" className="sp-btn-primary" disabled={!pick || busy === 'buy'} onClick={buy}>{busy === 'buy' ? 'Getting it…' : `Take this number · ${PRICE}`}</button>
        </div>
    ) : (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-primary" disabled={busy === 'find' || (kind === 'landline' && !areaOk)} onClick={find}>{busy === 'find' ? 'Finding numbers…' : 'Find numbers'}</button>
        </div>
    );

    return (
        <SlidePanel ref={sheet} title="Get a number" onClose={onClose} width={480} className="bly-panel fd-form bn-panel" footer={footer}>
            <div className="sp-form">
                {error && <p className="sp-general-error" role="alert">{error}</p>}
                {!options && (
                    <>
                        <p className="bn-lead">What should this number do?</p>
                        <div className="bn-kinds" role="radiogroup" aria-label="Kind of number">
                            <button type="button" role="radio" aria-checked={kind === 'mobile'} className={`bn-kind${kind === 'mobile' ? ' is-on' : ''}`} onClick={() => { hapticTick(); setKind('mobile'); }}>
                                <b>Calls and texts</b>
                                <small>A mobile number (07). Bailey can answer it, and clients can text you and reply to your texts.</small>
                                <span className="bn-tag">Recommended</span>
                            </button>
                            <button type="button" role="radio" aria-checked={kind === 'landline'} className={`bn-kind${kind === 'landline' ? ' is-on' : ''}`} onClick={() => { hapticTick(); setKind('landline'); }}>
                                <b>Calls only</b>
                                <small>A local landline number (01 or 02). Bailey can answer it. It can’t send or receive texts.</small>
                            </button>
                        </div>
                        {kind === 'landline' && (
                            <div className="sp-field">
                                <label className="sp-label" htmlFor="bn-area">Area code <span className="fd-optional">Optional</span></label>
                                <input id="bn-area" className="sp-input" inputMode="numeric" maxLength={6} placeholder="e.g. 01603" value={area} onChange={(e) => setArea(e.target.value.replace(/[^\d\s]/g, ''))} />
                                {areaOk ? <p className="sp-hint">So the number looks local. Leave it empty for any UK number.</p> : <p className="sp-error">Area codes start with 0 and have 3 to 5 digits.</p>}
                            </div>
                        )}
                        <div className="sp-field">
                            <label className="sp-label" htmlFor="bn-label">Name it <span className="fd-optional">Optional</span></label>
                            <input id="bn-label" className="sp-input" maxLength={40} placeholder="e.g. Front desk" value={label} onChange={(e) => setLabel(e.target.value)} />
                        </div>
                        <p className="bn-price">{PRICE}, added to your Beu plan. Give it back any time.</p>
                    </>
                )}
                {options && (
                    <>
                        <p className="bn-lead">{options.length ? 'Pick one. You can’t get the same number back once it’s given up, so choose one you’d put on a shop window.' : 'Nothing is free right now. Go back and try again, or a different area code.'}</p>
                        <div className="fd-pick" role="radiogroup" aria-label="Choose a number">
                            {options.map((n) => (
                                <button key={n.number} type="button" role="radio" aria-checked={pick === n.number} className={`fd-pick-row${pick === n.number ? ' is-on' : ''}`} onClick={() => { hapticTick(); setPick(n.number); }}>
                                    <span className="fd-radio" aria-hidden="true" />
                                    <b>{prettyNumber(n.number)}</b>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </SlidePanel>
    );
}

function MovePanel({ number, sites, onClose, onMoved }) {
    const sheet = useRef(null);
    const [to, setTo] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const go = async () => {
        setBusy(true); setError('');
        try {
            const list = await api.move(number.id, to);
            hapticSuccess();
            toast.success(`${prettyNumber(number.number)} moved`);
            onMoved(list);
            if (sheet.current) sheet.current.handleClose(); else onClose();
        } catch (e) { hapticError(); setError(e.message || 'It couldn’t be moved.'); setBusy(false); }
    };
    return (
        <SlidePanel ref={sheet} title="Move to another location" onClose={onClose} width={480} className="bly-panel fd-form bn-panel"
            footer={<div className="fd-panel-footer"><button type="button" className="sp-btn-primary" disabled={!to || busy} onClick={go}>{busy ? 'Moving…' : 'Move it'}</button></div>}>
            <div className="sp-form">
                {error && <p className="sp-general-error" role="alert">{error}</p>}
                <p className="bn-lead">{prettyNumber(number.number)} will belong to the location you pick. Its jobs switch off there until you turn them on, so nothing goes to the wrong place.</p>
                <div className="fd-pick" role="radiogroup" aria-label="Location">
                    {sites.map((s) => (
                        <button key={String(s.salonId)} type="button" role="radio" aria-checked={to === String(s.salonId)} className={`fd-pick-row${to === String(s.salonId) ? ' is-on' : ''}`} onClick={() => { hapticTick(); setTo(String(s.salonId)); }}>
                            <span className="fd-radio" aria-hidden="true" /><b>{s.salonName}</b>
                        </button>
                    ))}
                </div>
            </div>
        </SlidePanel>
    );
}

function BusinessNumbers(props) {
    const { sites, activeSalonId } = useSites();
    const [numbers, setNumbers] = useState(null);
    const [error, setError] = useState('');
    const [buying, setBuying] = useState(false);
    const [moving, setMoving] = useState(null);
    const [releasing, setReleasing] = useState(null);
    const [busyId, setBusyId] = useState('');
    const [editing, setEditing] = useState(null);
    const [labelDraft, setLabelDraft] = useState('');
    const params = new URLSearchParams((props.location && props.location.search) || '');
    const returnTo = params.get('return');
    const [justBought, setJustBought] = useState(false);

    const load = useCallback(() => { api.list().then(setNumbers).catch((e) => { setNumbers([]); setError(e.message); }); }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (params.get('buy') === '1') setBuying(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const change = async (n, patch) => {
        setBusyId(n.id); setError('');
        try { setNumbers(await api.update(n.id, patch)); }
        catch (e) { hapticError(); setError(e.message); }
        setBusyId('');
    };
    const release = async () => {
        const n = releasing;
        setBusyId(n.id);
        try {
            setNumbers(await api.release(n.id));
            hapticSuccess();
            toast.success(`${prettyNumber(n.number)} given back`);
            setReleasing(null);
        } catch (e) { hapticError(); setError(e.message); setReleasing(null); }
        setBusyId('');
    };
    const otherOwned = (sites || []).filter((s) => s.role === 'owner' && String(s.salonId) !== String(activeSalonId));
    // Buying, giving back and moving a number change what the owner pays
    // (£ a month on their plan): owner only. Anyone with `settings` can still
    // choose what each number does (businessNumberRoute).
    const ownerHere = isOwnerHere();
    const textsNumber = (numbers || []).find((n) => n.uses.texts);

    return (
        <SettingsPageLayout activeTab="business-numbers" {...props}>
            <div className="catalogue-content-wrapper bn-page">
                <div className="bn-head">
                    <div>
                        <h2>Business numbers</h2>
                        <p>Phone numbers for this location. Choose what each one does. {PRICE} each.</p>
                    </div>
                    {ownerHere && <button type="button" className="bn-primary" onClick={() => { hapticTick(); setBuying(true); }}>Get a number</button>}
                </div>

                {justBought && returnTo && (
                    <div className="bn-return">
                        <span>Your number is ready.</span>
                        <button type="button" className="bn-primary" onClick={() => props.history.push(returnTo)}>Back to where you were</button>
                    </div>
                )}
                {error && <p className="sp-general-error" role="alert">{error}</p>}

                {numbers === null && <div className="bn-card bn-skeleton" />}

                {numbers && !numbers.length && (
                    <div className="bn-empty">
                        <b>No numbers yet</b>
                        <p>Get a number so Bailey can answer your calls, and so clients can text you and reply to your texts.</p>
                        {ownerHere
                            ? <button type="button" className="bn-primary" onClick={() => setBuying(true)}>Get a number</button>
                            : <p>Only the owner can get a number, as it’s added to their plan.</p>}
                    </div>
                )}

                {numbers && numbers.map((n) => (
                    <div className="bn-card" key={n.id}>
                        <div className="bn-card-top">
                            <div className="bn-number">
                                <b>{prettyNumber(n.number)}</b>
                                {editing === n.id ? (
                                    <span className="bn-rename">
                                        <label htmlFor={`bn-l-${n.id}`} className="bn-sr">Name</label>
                                        <input id={`bn-l-${n.id}`} className="sp-input" maxLength={40} value={labelDraft} autoFocus onChange={(e) => setLabelDraft(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { change(n, { label: labelDraft }); setEditing(null); } if (e.key === 'Escape') setEditing(null); }} />
                                        <button type="button" className="bn-link" onClick={() => { change(n, { label: labelDraft }); setEditing(null); }}>Save</button>
                                    </span>
                                ) : (
                                    <button type="button" className="bn-name" onClick={() => { setEditing(n.id); setLabelDraft(n.label); }}>{n.label || 'Add a name'}</button>
                                )}
                            </div>
                            <span className={`bn-kind-tag${n.canText ? ' is-mobile' : ''}`}>{n.kind}</span>
                        </div>
                        <div className="bn-jobs">
                            <div className="bn-job">
                                <div><b>Bailey answers calls</b><small>{n.uses.calls ? 'Calls to this number reach Bailey.' : 'Calls to this number aren’t answered by Bailey.'}</small></div>
                                <Switch on={n.uses.calls} disabled={busyId === n.id} label="Bailey answers calls" onChange={(v) => change(n, { calls: v })} />
                            </div>
                            <div className="bn-job">
                                <div>
                                    <b>Two-way texts</b>
                                    <small>{!n.canText ? 'A landline can’t send or receive texts.'
                                        : n.uses.texts ? 'Your texts go out from this number and client replies come back here.'
                                            : textsNumber ? `${prettyNumber(textsNumber.number)} does texts now. Turn this on to switch.` : 'Turn on so clients can reply to your texts.'}</small>
                                </div>
                                <Switch on={n.uses.texts} disabled={!n.canText || busyId === n.id} label="Two-way texts" onChange={(v) => change(n, { texts: v })} />
                            </div>
                        </div>
                        {n.activity && (
                            <div className="bn-activity" aria-label="This month">
                                <span className="bn-activity-label">This month</span>
                                {n.uses.calls || n.activity.calls ? <span><b>{n.activity.calls}</b> {n.activity.calls === 1 ? 'call' : 'calls'} · <b>{n.activity.minutes}</b> min</span> : null}
                                {n.canText ? <span><b>{n.activity.textsSent}</b> {n.activity.textsSent === 1 ? 'text' : 'texts'} sent · <b>{n.activity.replies}</b> {n.activity.replies === 1 ? 'reply' : 'replies'}</span> : null}
                            </div>
                        )}
                        <div className="bn-card-foot">
                            <span>{PRICE}</span>
                            {ownerHere && otherOwned.length > 0 && <button type="button" className="bn-link" onClick={() => setMoving(n)}>Move to another location</button>}
                            {ownerHere && <button type="button" className="bn-link is-danger" onClick={() => setReleasing(n)}>Give it back</button>}
                        </div>
                    </div>
                ))}

                <p className="bn-note">Already have a salon number clients know? Ask your phone provider to divert it to your Beu number, so clients keep ringing the number they know and Bailey answers.</p>
            </div>

            {buying && ownerHere && <BuyPanel onClose={() => setBuying(false)} onBought={(list) => { if (list) setNumbers(list); else load(); setJustBought(true); }} />}
            {moving && <MovePanel number={moving} sites={otherOwned} onClose={() => setMoving(null)} onMoved={(list) => setNumbers(list)} />}
            {releasing && (
                <ConfirmCard
                    title="Give this number back?"
                    text={`${prettyNumber(releasing.number)} stops working straight away: calls and texts to it go nowhere, and you can’t get the same number again. Billing stops at the end of the month.`}
                    confirm="Give it back"
                    busyText="Giving it back…"
                    busy={busyId === releasing.id}
                    onCancel={() => setReleasing(null)}
                    onConfirm={release}
                />
            )}
        </SettingsPageLayout>
    );
}

export default connect((state) => ({ user: state.user }))(BusinessNumbers);
